import EditIcon from '@mui/icons-material/Edit';
import LockIcon from '@mui/icons-material/Lock';
import List from '@mui/material/List/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import { remove, post, put } from '../../Shared/ajax';
import { MouseEvent, useContext, useState } from 'react';
import {
  WorkflowEngineActionKind,
  WorkflowEngineContext
} from '../../WorkflowEngine/workflowEngineReducer';
import { useSnackbar } from 'notistack';
import { getErrorMessage } from '../../Shared/errorHandling';
import IconButton from '@mui/material/IconButton';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import Popover from '@mui/material/Popover';
import Stack from '@mui/material/Stack';
import ConfirmationDialog from '../../Shared/ConfirmationDialog';
import {
  UpdateDynamicWorkflowPausedUnpausedResponse,
  UpdateDynamicWorkflowPausedUnpausedRequest,
  KebabMenuAction
} from './types';
import LockStatus from '../LockStatus';
import { AppContext } from '../../App/appReducer';
import { AppActionKind, AppPageKind } from '../../App/types';

export default () => {
  const pauseText = 'Pause Live';
  const unpauseText = 'Unpause Live';
  const pauseMessageText = 'Workflow is Paused!';
  const unpauseMessageText = 'Workflow is Unpaused!';

  const { state, dispatch } = useContext(WorkflowEngineContext);
  const { dispatch: appDispatch } = useContext(AppContext);

  const {
    versionExternalId,
    versionData,
    publishedVersionExternalId,
    isPaused,
    lockStatus
  } = state;

  const [showConfirmDialog, setShowConfirmDialog] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [pauseMenuText, setPausedMenuText] = useState<string>(
    isPaused ? unpauseText : pauseText
  );
  const [kebabAnchorEl, setKebabAnchorEl] = useState<HTMLButtonElement | null>(
    null
  );

  const isKebabPopoverOpen = Boolean(kebabAnchorEl);

  const { enqueueSnackbar } = useSnackbar();

  const iconStyle = {
    paddingLeft: '15px',
    fontSize: '20px'
  };

  const handleKebabClick = (event: MouseEvent<HTMLButtonElement>) => {
    setShowConfirmDialog(false);
    setKebabAnchorEl(isKebabPopoverOpen ? null : event.currentTarget);
  };

  const handleClose = () => {
    setKebabAnchorEl(null);
  };

  const handleConfirmCancelClick = () => {
    setShowConfirmDialog(false);
    setKebabAnchorEl(null);
  };

  const switchToEditMode = (versionExternalId: string) => {
    dispatch({
      type: WorkflowEngineActionKind.SWITCH_TO_EDIT_MODE,
      payload: versionExternalId
    });
  };

  const switchToLanding = () => {
    appDispatch({
      type: AppActionKind.SET_APPPAGE,
      payload: AppPageKind.LANDING
    });
  };

  const handleEditClick = async () => {
    setIsLoading(true);
    try {
      const url = `DynamicWorkflowVersion/${versionExternalId}`;
      const response = await post<
        {
          versionExternalId: string;
        },
        undefined
      >(url, undefined);
      switchToEditMode(response.versionExternalId);
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
    setIsLoading(false);
  };

  const handleDiscardConfirmClick = async () => {
    setIsLoading(true);
    try {
      const url = `DynamicWorkflowVersion/${versionExternalId}`;
      const publishedVersionExternalId = await remove<string | undefined>(url);
      if (publishedVersionExternalId) {
        switchToEditMode(publishedVersionExternalId);
      } else {
        switchToLanding();
      }
    } catch (err) {
      enqueueSnackbar(getErrorMessage(err), { variant: 'error' });
    }
    setIsLoading(false);
  };

  const handleDiscardDraftClick = () => {
    setShowConfirmDialog(true);
  };

  const handleViewPublishedClick = () => {
    if (!publishedVersionExternalId) return;
    switchToEditMode(publishedVersionExternalId);
  };

  const handlePauseUnpauseClick = async () => {
    try {
      const newPausedState = !isPaused;
      await put<
        UpdateDynamicWorkflowPausedUnpausedResponse,
        UpdateDynamicWorkflowPausedUnpausedRequest
      >(`DynamicWorkflow/PauseUnpauseWorkflow`, {
        dynamicWorkflowVersionExternalId: versionExternalId,
        isPaused: newPausedState
      });

      let successMessage = '';
      if (newPausedState) {
        successMessage = pauseMessageText;
        setPausedMenuText(unpauseText);
      } else {
        successMessage = unpauseMessageText;
        setPausedMenuText(pauseText);
      }

      dispatch({
        type: WorkflowEngineActionKind.SET_WORKFLOW_PAUSED_UNPAUSED,
        payload: newPausedState
      });

      enqueueSnackbar(successMessage, {
        variant: 'success'
      });
    } catch (err: unknown) {
      enqueueSnackbar((err as Error)?.message, { variant: 'error' });
    }
  };

  const unpublishedActions: KebabMenuAction[] = [
    {
      key: 'discardDraft',
      title: 'Discard Draft',
      icon: <EditIcon sx={iconStyle} />,
      confirm: {
        promptMsg:
          'Are you sure you want to discard this draft?\n\nThis action will permanently delete the entire unpublished workflow and cannot be undone.',
        confirmBtnText: 'Discard',
        onConfirmClick: handleDiscardConfirmClick,
        onCancelClick: handleConfirmCancelClick
      },
      onClick: handleDiscardDraftClick
    }
  ];

  if (publishedVersionExternalId) {
    unpublishedActions.push({
      key: 'viewPublished',
      title: 'View Published',
      icon: <PauseCircleOutlineIcon sx={iconStyle} />,
      onClick: handleViewPublishedClick
    });
  }

  const publishedActions: KebabMenuAction[] = [
    {
      key: 'edit',
      title: 'Edit',
      icon: lockStatus?.isLockedByOtherUser ? (
        <LockIcon sx={iconStyle} />
      ) : (
        <EditIcon sx={iconStyle} />
      ),
      disabled: lockStatus?.isLockedByOtherUser,
      enableLockStatus: lockStatus?.isLockedByOtherUser,
      onClick: handleEditClick
    },
    {
      key: 'pause',
      title: pauseMenuText,
      icon: <PauseCircleOutlineIcon sx={iconStyle} />,
      onClick: handlePauseUnpauseClick
    }
  ];

  const actions = versionData?.isPublished
    ? publishedActions
    : unpublishedActions;

  const renderActionButton = (action: KebabMenuAction) => (
    <ListItemButton
      sx={{ paddingLeft: '0px', paddingRight: '15px' }}
      onClick={action.onClick}
      disabled={isLoading || action.disabled}
    >
      <ListItemIcon>{action.icon}</ListItemIcon>
      <ListItemText primary={action.title} />
    </ListItemButton>
  );

  const renderAction = (action: KebabMenuAction) => (
    <Stack key={action.key}>
      {action.enableLockStatus && lockStatus ? (
        <LockStatus lockStatus={lockStatus}>
          {renderActionButton(action)}
        </LockStatus>
      ) : (
        renderActionButton(action)
      )}
      {action.confirm && (
        <ConfirmationDialog
          open={showConfirmDialog}
          promptMsg={action.confirm.promptMsg}
          confirmBtnText={action.confirm.confirmBtnText}
          onConfirmClick={action.confirm.onConfirmClick}
          onCancelClick={action.confirm.onCancelClick}
          processing={isLoading}
        />
      )}
    </Stack>
  );

  return (
    <>
      <IconButton aria-label="More" onClick={handleKebabClick}>
        <MoreVertIcon />
      </IconButton>
      <Popover
        open={isKebabPopoverOpen}
        anchorEl={kebabAnchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <List>{actions.map(renderAction)}</List>
      </Popover>
    </>
  );
};
